import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  History,
  CheckCircle,
  Clock,
  CalendarDays,
  Dog,
  FileText,
  Receipt,
  PlayCircle,
  Check,
  MessageCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Star } from 'lucide-react';
import api from '@/lib/api';

const SERVICE_LABELS = {
  paseo: 'Paseo',
  cuidado: 'Cuidado',
  daycare: 'Daycare',
  contacto_directo: 'Servicio directo'
};

function formatDate(dateStr) {
  if (!dateStr) return null;

  const d = new Date(dateStr);

  if (Number.isNaN(d.getTime())) return null;

  return d.toLocaleDateString('es-CL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

const ServiceCard = ({ service, active, onComplete, completing, cancelling, user, onReview, subscription, onRehire, onCancel }) => {
  const petName =
    service.pet?.name ||
    service.pet_name ||
    null;

  const serviceName =
    SERVICE_LABELS[service.service_type] ||
    service.service_type ||
    'Servicio de cuidado';

  const isClient = user?.user_id === service.client_user_id;
  const reviewDone = isClient ? service.client_review_done : service.provider_review_done;

  const otherPerson =
    isClient
      ? (service.provider_name || 'Cuidador U-CAN')
      : (service.client_name || 'Cliente U-CAN');

  const date =
    service.preferred_dates?.[0] ||
    service.start_date ||
    service.created_at;

  return (
    <div
      className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${
        active ? 'border-green-200' : 'border-gray-200'
      }`}
    >
      <div className="p-5 md:p-6">
        <div className="flex flex-col md:flex-row md:items-start gap-5">

          <div
            className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${
              active
                ? 'bg-green-100'
                : 'bg-gray-100'
            }`}
          >
            {active ? (
              <PlayCircle className="w-7 h-7 text-green-700" />
            ) : (
              <CheckCircle className="w-7 h-7 text-gray-500" />
            )}
          </div>

          <div className="flex-1 min-w-0">

            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">

              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-xl font-bold text-gray-900">
                    {serviceName}
                  </h3>

                  <span
                    className={`px-3 py-1 rounded-full text-xs font-bold ${
                      active
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {active ? 'EN CURSO' : service.status === 'cancelled' ? 'CANCELADO' : 'FINALIZADO'}
                  </span>
                </div>

                <p className="text-gray-600 mt-1">
                  {otherPerson}
                </p>
              </div>

              {date && (
                <div className="flex items-center gap-1.5 text-sm text-gray-500">
                  <CalendarDays className="w-4 h-4" />
                  {formatDate(date)}
                </div>
              )}

            </div>

            <div className="flex flex-wrap gap-3 mt-4">

              {petName && (
                <div className="inline-flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2 text-sm text-gray-700">
                  <Dog className="w-4 h-4 text-[#E6202E]" />
                  {petName}
                </div>
              )}

              {service.comuna && (
                <div className="inline-flex items-center bg-gray-50 rounded-xl px-3 py-2 text-sm text-gray-700">
                  {service.comuna}
                </div>
              )}

            </div>

            {service.description && (
              <p className="text-sm text-gray-500 mt-4 line-clamp-2">
                {service.description}
              </p>
            )}

          </div>
        </div>
      </div>

      {active && (
        <div className="border-t border-green-100 bg-green-50/60 p-4 md:px-6 space-y-2">
          <Link to="/chat">
            <Button
              variant="outline"
              className="w-full bg-white rounded-xl py-5 font-bold"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Abrir chat
            </Button>
          </Link>

          <Button
            onClick={() => onComplete(service)}
            disabled={completing || cancelling}
            className="w-full bg-green-600 hover:bg-green-700 text-white rounded-xl py-6 text-base md:text-lg font-bold shadow-sm"
          >
            <Check className="w-5 h-5 mr-2" />
            {completing ? 'Finalizando...' : 'Finalizar servicio'}
          </Button>

          <Button
            type="button"
            onClick={() => onCancel(service)}
            disabled={completing || cancelling}
            variant="outline"
            className="w-full rounded-xl py-5 font-bold text-red-600 border-red-200 hover:bg-red-50 bg-white"
          >
            {cancelling ? 'Cancelando...' : 'Cancelar servicio'}
          </Button>

          <p className="text-xs text-green-800 text-center mt-2">
            Coordina los detalles por chat y finaliza el servicio cuando termine.
          </p>
        </div>
      )}

      {!active && (
        <div className="border-t bg-gray-50 p-4 md:px-6">
          <p className="text-xs text-gray-500 mb-3">
            {service.status === 'cancelled' ? 'Cancelado' : 'Finalizado'} {formatDate(service.completed_at || service.cancelled_at) || ''}
          </p>

          {service.status === 'cancelled' ? (
            <div className="w-full rounded-xl bg-gray-100 border border-gray-200 px-4 py-4 text-center text-gray-600">
              <p className="text-sm font-bold">Servicio cancelado</p>
              {service.cancel_reason && <p className="text-xs mt-1">Motivo: {service.cancel_reason}</p>}
            </div>
          ) : reviewDone ? (
            <div className="w-full rounded-xl bg-green-100 border border-green-200 px-4 py-4 flex items-center justify-center gap-2 text-green-700">
              <Star className="w-5 h-5 fill-green-600 text-green-600" />
              <div>
                <p className="text-sm font-bold">
                  Evaluación enviada
                </p>
                <p className="text-xs text-green-600 font-medium">
                  Gracias por compartir tu experiencia
                </p>
              </div>
            </div>
          ) : (
            <Button
              onClick={() => onReview(service)}
              className="w-full bg-[#E6202E] hover:bg-[#D31522] text-white rounded-xl py-5 font-bold"
            >
              <Star className="w-4 h-4 mr-2 fill-white" />
              Calificar experiencia
            </Button>
          )}

          {isClient && service.status !== 'cancelled' && (
            <div className="mt-3">
              {subscription?.has_subscription ? (
                <Button
                  type="button"
                  onClick={() => onRehire(service)}
                  variant="outline"
                  className="w-full rounded-xl py-5 font-bold bg-white"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Volver a contratar
                </Button>
              ) : (
                <Link to="/planes">
                  <Button
                    type="button"
                    className="w-full rounded-xl py-5 font-bold bg-green-600 hover:bg-green-700 text-white"
                  >
                    Activar Premium para recontratar
                  </Button>
                </Link>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};


export default function ServiceHistory() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeServices, setActiveServices] = useState([]);
  const [history, setHistory] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'en-curso');
  const [completingId, setCompletingId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [user, setUser] = useState(null);
  const [reviewingService, setReviewingService] = useState(null);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [savingReview, setSavingReview] = useState(false);
  const [rehireService, setRehireService] = useState(null);
  const [rehirePetId, setRehirePetId] = useState('');
  const [rehireMessage, setRehireMessage] = useState('');
  const [sendingRehire, setSendingRehire] = useState(false);
  const [cancelService, setCancelService] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const changeTab = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const loadData = async () => {
    try {
      const [userRes, activeRes, historyRes, invoicesRes, subRes, petsRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/service-cycles/active').catch(() => ({ data: [] })),
        api.get('/service-cycles/history').catch(() => ({ data: [] })),
        api.get('/subscription/invoices').catch(() => ({ data: [] })),
        api.get('/subscription/my').catch(() => ({ data: { has_subscription: false } })),
        api.get('/pets').catch(() => ({ data: [] }))
      ]);

      setUser(userRes.data);
      setActiveServices(activeRes.data || []);
      setHistory(historyRes.data || []);
      setInvoices(invoicesRes.data || []);
      setSubscription(subRes.data);
      setPets(petsRes.data || []);

    } catch {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };


  const handleComplete = async (service) => {
    const confirmed = window.confirm(
      '¿Confirmas que este servicio ya finalizó? Después podrás dejar tu evaluación.'
    );

    if (!confirmed) return;

    setCompletingId(service.service_id);

    try {
      await api.put(
        `/service-cycles/${service.service_id}/complete`,
        { note: null }
      );

      toast.success('Servicio finalizado correctamente');

      changeTab('finalizados');

      setReviewingService(service);
      setReviewRating(0);
      setReviewComment('');

      await loadData();

    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
        'No pudimos finalizar el servicio'
      );
    } finally {
      setCompletingId(null);
    }
  };

  const openCancel = (service) => {
    setCancelService(service);
    setCancelReason('');
  };

  const submitCancel = async () => {
    if (!cancelService) return;

    if (!cancelReason.trim() || cancelReason.trim().length < 5) {
      toast.error('Escribe un motivo breve para cancelar');
      return;
    }

    setCancellingId(cancelService.service_id);

    try {
      await api.put(`/service-cycles/${cancelService.service_id}/cancel`, {
        reason: cancelReason.trim()
      });

      toast.success('Servicio cancelado');
      setCancelService(null);
      setCancelReason('');
      changeTab('finalizados');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No pudimos cancelar el servicio');
    } finally {
      setCancellingId(null);
    }
  };





  const openReview = (service) => {
    setReviewingService(service);
    setReviewRating(0);
    setReviewComment('');
  };

  const openRehire = (service) => {
    setRehireService(service);
    setRehirePetId(service.pet?.pet_id || '');
    setRehireMessage(`Hola, me gustaría volver a contratarte para un nuevo servicio con ${service.pet?.name || 'mi mascota'}.`);
  };

  const submitRehire = async () => {
    if (!rehireService?.provider_user_id) {
      toast.error('No pudimos identificar al cuidador');
      return;
    }

    if (!rehirePetId) {
      toast.error('Selecciona una mascota');
      return;
    }

    setSendingRehire(true);

    try {
      await api.post('/contact-requests', {
        provider_user_id: rehireService.provider_user_id,
        pet_id: rehirePetId,
        message: rehireMessage || 'Hola, me gustaría volver a contratarte para un nuevo servicio.'
      });

      toast.success('Solicitud enviada. Te avisaremos cuando el cuidador responda.');
      setRehireService(null);
      setRehirePetId('');
      setRehireMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No pudimos enviar la solicitud');
    } finally {
      setSendingRehire(false);
    }
  };


  const submitReview = async () => {
    if (!reviewingService || !user) return;

    if (reviewRating === 0) {
      toast.error('Selecciona una calificación de 1 a 5 estrellas');
      return;
    }

    if (!reviewComment.trim()) {
      toast.error('Escribe un comentario sobre tu experiencia');
      return;
    }

    if (reviewComment.trim().length < 10) {
      toast.error('El comentario debe tener al menos 10 caracteres');
      return;
    }

    setSavingReview(true);

    try {
      const isClient = user.user_id === reviewingService.client_user_id;

      if (isClient) {
        await api.post('/reviews', {
          provider_id: reviewingService.provider_id,
          service_cycle_id: reviewingService.service_id,
          rating: reviewRating,
          comment: reviewComment.trim(),
          photos: []
        });
      } else {
        await api.post('/reviews/client', {
          client_user_id: reviewingService.client_user_id,
          service_cycle_id: reviewingService.service_id,
          rating: reviewRating,
          communication: reviewRating,
          comment: reviewComment
        });
      }

      toast.success('Evaluación enviada. ¡Gracias!');
      setReviewingService(null);
      await loadData();

    } catch (error) {
      toast.error(error.response?.data?.detail || 'No pudimos guardar tu evaluación');
    } finally {
      setSavingReview(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin w-16 h-16 border-4 border-[#E6202E] border-t-transparent rounded-full" />
      </div>
    );
  }


  return (
    <div
      className="min-h-screen bg-gray-50"
      data-testid="service-history-page"
    >
      <div className="max-w-5xl mx-auto px-4 py-8 md:py-10">

        <div className="mb-8">
          <p className="text-sm font-semibold text-[#E6202E] mb-2">
            Actividad U-CAN
          </p>

          <h1 className="text-3xl md:text-4xl font-black text-gray-900">
            Mis servicios
          </h1>

          <p className="text-gray-500 mt-2">
            Revisa tus servicios en curso, finalizados y tus facturas.
          </p>
        </div>


        <div className="flex gap-2 mb-7 border-b overflow-x-auto">

          <button
            onClick={() => changeTab('en-curso')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${
              activeTab === 'en-curso'
                ? 'border-[#E6202E] text-[#E6202E]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Clock className="w-4 h-4" />
            En curso ({activeServices.length})
          </button>

          <button
            onClick={() => changeTab('finalizados')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${
              activeTab === 'finalizados'
                ? 'border-[#E6202E] text-[#E6202E]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <History className="w-4 h-4" />
            Finalizados ({history.length})
          </button>

          <button
            onClick={() => changeTab('invoices')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${
              activeTab === 'invoices'
                ? 'border-[#E6202E] text-[#E6202E]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Receipt className="w-4 h-4" />
            Facturas ({invoices.length})
          </button>

        </div>


        {activeTab === 'en-curso' && (
          <div className="space-y-4">
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-gray-900">
                Servicios en curso
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Servicios aceptados que todavía no han finalizado.
              </p>
            </div>

            {activeServices.length === 0 ? (
              <div className="bg-white rounded-2xl border p-8 text-center">
                <Clock className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <p className="font-semibold text-gray-700">
                  No tienes servicios en curso
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  Cuando una propuesta o contacto directo sea aceptado, aparecerá aquí.
                </p>
              </div>
            ) : (
              activeServices.map(service => (
                <ServiceCard
                  key={service.service_id}
                  service={service}
                  active
                  onComplete={handleComplete}
                  completing={completingId === service.service_id}
                  cancelling={cancellingId === service.service_id}
                  user={user}
                  onReview={openReview}
                  onCancel={openCancel}
                  subscription={subscription}
                  onRehire={openRehire}
                />
              ))
            )}
          </div>
        )}

        {activeTab === 'finalizados' && (
          <div className="space-y-4">
            <div className="mb-4">
              <h2 className="text-2xl font-bold text-gray-900">
                Servicios finalizados
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Califica tus experiencias pendientes o revisa las que ya evaluaste.
              </p>
            </div>

            {history.length === 0 ? (
              <div className="bg-white rounded-2xl border p-8 text-center">
                <History className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <p className="font-semibold text-gray-700">
                  Todavía no tienes servicios finalizados
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  Tu historial aparecerá aquí cuando completes tu primer servicio.
                </p>
              </div>
            ) : (
              history.map(service => (
                <ServiceCard
                  key={service.service_id}
                  service={service}
                  active={false}
                  user={user}
                  onReview={openReview}
                  subscription={subscription}
                  onRehire={openRehire}
                />
              ))
            )}
          </div>
        )}


        {activeTab === 'invoices' && (
          <div>
            {invoices.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center shadow-sm border">
                <Receipt className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                <p className="text-gray-500 text-lg mb-2">
                  Sin facturas
                </p>
                <p className="text-sm text-gray-400">
                  Tus pagos de suscripción aparecerán aquí.
                </p>
              </div>
            ) : (
              <div className="space-y-3">

                {invoices.map((inv, idx) => {
                  const isPaid = inv.status === 'active';
                  const isPending = inv.status === 'pending';

                  return (
                    <div
                      key={inv.subscription_id || idx}
                      className="bg-white rounded-xl p-5 shadow-sm border"
                    >
                      <div className="flex items-center gap-4">

                        <div
                          className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                            isPaid
                              ? 'bg-green-50'
                              : isPending
                              ? 'bg-yellow-50'
                              : 'bg-gray-50'
                          }`}
                        >
                          <FileText
                            className={`w-6 h-6 ${
                              isPaid
                                ? 'text-green-600'
                                : isPending
                                ? 'text-yellow-600'
                                : 'text-gray-400'
                            }`}
                          />
                        </div>

                        <div className="flex-1 min-w-0">

                          <div className="flex items-center justify-between gap-2">
                            <h3 className="font-bold text-gray-900">
                              {inv.plan_name}
                            </h3>

                            <span className="text-[#E6202E] font-bold text-lg">
                              ${inv.amount?.toLocaleString('es-CL')}
                            </span>
                          </div>

                          <div className="flex items-center gap-3 mt-1">

                            <span
                              className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                isPaid
                                  ? 'bg-green-100 text-green-700'
                                  : isPending
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {isPaid
                                ? 'Pagado'
                                : isPending
                                ? 'Pendiente'
                                : inv.status === 'cancelled'
                                ? 'Cancelado'
                                : inv.status}
                            </span>

                            {inv.start_date && (
                              <span className="text-xs text-gray-400">
                                {formatDate(inv.start_date)} - {formatDate(inv.end_date)}
                              </span>
                            )}

                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

              </div>
            )}
          </div>
        )}

      </div>

      {cancelService && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6">
            <h2 className="text-2xl font-black text-gray-900">
              Cancelar servicio
            </h2>

            <p className="text-sm text-gray-500 mt-2">
              Cuéntanos brevemente por qué necesitas cancelar este servicio.
            </p>

            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={4}
              className="w-full rounded-2xl border border-gray-200 p-4 text-sm mt-4 focus:outline-none focus:ring-2 focus:ring-[#E6202E]/20"
              placeholder="Ej: No podré realizar el servicio en la fecha acordada..."
            />

            <div className="grid grid-cols-2 gap-3 mt-5">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCancelService(null)}
                disabled={cancellingId === cancelService.service_id}
                className="rounded-xl"
              >
                Volver
              </Button>

              <Button
                type="button"
                onClick={submitCancel}
                disabled={cancellingId === cancelService.service_id}
                className="rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold"
              >
                {cancellingId === cancelService.service_id ? 'Cancelando...' : 'Confirmar cancelación'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {rehireService && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6">
            <h2 className="text-2xl font-black text-gray-900">
              Volver a contratar
            </h2>

            <p className="text-sm text-gray-500 mt-2">
              Envía una nueva solicitud a {rehireService.provider_name || 'tu cuidador'}.
            </p>

            <div className="mt-5">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Mascota
              </label>
              <select
                value={rehirePetId}
                onChange={(e) => setRehirePetId(e.target.value)}
                className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6202E]/20"
              >
                <option value="">Selecciona una mascota</option>
                {pets.map(pet => (
                  <option key={pet.pet_id} value={pet.pet_id}>
                    {pet.name}
                  </option>
                ))}
              </select>
            </div>

            <textarea
              value={rehireMessage}
              onChange={(e) => setRehireMessage(e.target.value)}
              rows={4}
              className="w-full rounded-2xl border border-gray-200 p-4 text-sm mt-4 focus:outline-none focus:ring-2 focus:ring-[#E6202E]/20"
              placeholder="Escribe un mensaje para el cuidador..."
            />

            <div className="grid grid-cols-2 gap-3 mt-5">
              <Button
                type="button"
                variant="outline"
                onClick={() => setRehireService(null)}
                disabled={sendingRehire}
                className="rounded-xl"
              >
                Cancelar
              </Button>

              <Button
                type="button"
                onClick={submitRehire}
                disabled={sendingRehire}
                className="rounded-xl bg-green-600 hover:bg-green-700 text-white font-bold"
              >
                {sendingRehire ? 'Enviando...' : 'Enviar solicitud'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {reviewingService && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6">
            <h2 className="text-2xl font-black text-gray-900">
              Calificar experiencia
            </h2>

            <p className="text-sm text-gray-500 mt-2">
              {user?.user_id === reviewingService.client_user_id
                ? `Evalúa a ${reviewingService.provider_name || 'tu cuidador'}`
                : `Evalúa a ${reviewingService.client_name || 'tu cliente'}`}
            </p>

            <div className="flex justify-center gap-2 my-6">
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setReviewRating(n)}
                  className="p-1"
                >
                  <Star
                    className={`w-10 h-10 ${
                      n <= reviewRating
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
            </div>

            <textarea
              value={reviewComment}
              onChange={(e) => setReviewComment(e.target.value)}
              rows={4}
              required
              minLength={10}
              className="w-full rounded-2xl border border-gray-200 p-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#E6202E]/20"
              placeholder="Cuéntanos cómo fue la experiencia..."
            />

            <p className="text-xs text-gray-400 mt-2">
              La calificación y el comentario son obligatorios.
            </p>

            <div className="grid grid-cols-2 gap-3 mt-5">
              <Button
                type="button"
                variant="outline"
                onClick={() => setReviewingService(null)}
                disabled={savingReview}
                className="rounded-xl"
              >
                Después
              </Button>

              <Button
                type="button"
                onClick={submitReview}
                disabled={savingReview}
                className="rounded-xl bg-green-600 hover:bg-green-700 text-white font-bold"
              >
                {savingReview ? 'Enviando...' : 'Enviar evaluación'}
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
